import {
  Button,
  Checkbox,
  CheckboxGroup,
  CloseButton,
  Flex,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import { useEffect, useState } from "react";
import { TeamViewModel } from "../../../models/Team";
import { CompanyFormData } from "./types";
import { getCompanies } from "../../../services/companyService";
import { useCompany } from "../../../contexts/CompanyContext";
import { saveTeam, updateTeam } from "../../../services/teamService";
import { useToast } from "@chakra-ui/react";

const CreateTeamModal = ({
  isOpen,
  onClose,
  onSaved,
  team,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  team?: TeamViewModel;
}) => {
  const toast = useToast();
  const { currentCompany } = useCompany();
  const [name, setName] = useState("");
  const [clients, setClients] = useState<CompanyFormData[]>([]);
  const [filteredClients, setFilteredClients] = useState<CompanyFormData[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const load = async () => {
      const response = await getCompanies(true, currentCompany);
      if (response) {
        setClients(response);
        setFilteredClients(response);
      }
    };
    if (isOpen) load();
  }, [isOpen, currentCompany]);

  useEffect(() => {
    if (isOpen) {
      setName(team?.name ?? "");
      setSelectedClients(team?.clientsIds ?? []);
      setError(undefined);
    }
  }, [isOpen, team]);

  const handleSave = async () => {
    setError(undefined);
    if (name.trim().length === 0) {
      setError("Informe um nome para a equipe.");
      return;
    }
    setLoading(true);
    const payload: TeamViewModel = {
      id: team?.id,
      name: name.trim(),
      clientsIds: selectedClients,
    };
    const response = team?.id ? await updateTeam(payload) : await saveTeam(payload);
    setLoading(false);

    if (response?.data?.success) {
      toast({
        title: team?.id ? "Equipe atualizada." : "Equipe criada.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSaved?.();
      onClose();
    } else {
      toast({
        title: response?.data?.notifications?.[0] ?? "Erro ao salvar equipe.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)">
        <ModalHeader>
          <Flex justifyContent="space-between" alignItems="center">
            <Heading size="md" fontWeight="semibold">
              {team?.id ? "Editar equipe" : "Nova equipe"}
            </Heading>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <FormLabel>Nome da equipe:</FormLabel>
          <Input
            mb="16px"
            placeholder="Ex.: Equipe São Paulo"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <FormLabel>Clientes que a equipe acessa:</FormLabel>
          <Input
            mb="12px"
            placeholder="Pesquise por nome"
            onChange={(e) =>
              setFilteredClients(() =>
                clients.filter((c) =>
                  c.name.toLowerCase().includes(e.target.value.toLowerCase())
                )
              )
            }
          />
          <Checkbox
            isChecked={
              selectedClients.length > 0 &&
              clients.every((c) => selectedClients.includes(c.id))
            }
            onChange={(e) => {
              setSelectedClients(e.target.checked ? clients.map((c) => c.id) : []);
            }}
            mb="16px"
          >
            Selecionar todas
          </Checkbox>
          <Flex flexDir="column" maxHeight="35vh" overflowY="auto" mb="8px">
            <CheckboxGroup
              value={selectedClients}
              onChange={(e) => setSelectedClients(e?.map((c) => c.toString()))}
            >
              <Flex flexDirection="column">
                {filteredClients?.map((c) => (
                  <Checkbox key={c.id} value={c.id}>
                    {c.name}
                  </Checkbox>
                ))}
              </Flex>
            </CheckboxGroup>
          </Flex>

          {error && (
            <Text mt="8px" color={crm.danger}>
              {error}
            </Text>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`}>
          <Flex w="100%" justifyContent="flex-end" gap="12px">
            <Button
              variant="outline"
              borderColor={crm.border}
              color={crm.brand}
              borderRadius="10px"
              _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              backgroundColor={crm.brand}
              color="white"
              borderRadius="10px"
              _hover={{ backgroundColor: crm.brandDark }}
              isLoading={loading}
              onClick={handleSave}
            >
              Salvar
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateTeamModal;
